/**
 * @fileoverview Vendor Info Modal Component
 *
 * Modal dialog for viewing vendor information quickly from anywhere in the portal.
 * Displays all vendor details including contact info, address, website, and notes.
 *
 * @requires react
 * @requires react-icons/fa
 * @version 1.0.0
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaTimes,
  FaTools,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaGlobe,
  FaStickyNote,
  FaSpinner,
  FaExclamationTriangle,
  FaUserTie,
  FaExternalLinkAlt,
  FaPaperclip,
  FaDownload,
  FaStore,
  FaUsers,
  FaEdit,
  FaProjectDiagram,
} from "react-icons/fa";
import {
  useAdminVendor,
  useCreateVendor,
  useUpdateVendor,
} from "../../hooks/useAdminVendors";
import VendorModal from "./VendorModal";
import { formatPhoneDisplay } from "../../utils/phoneUtils";
import "./VendorInfoModal.css";

/**
 * Format file size in bytes to human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
const formatFileSize = (bytes) => {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

/**
 * VendorInfoModal Component
 * @param {Object} props - Component props
 * @param {string} props.vendorId - Vendor record ID
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Close handler
 */
const VendorInfoModal = ({ vendorId, isOpen, onClose, showTestAccounts = false }) => {
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const {
    data: vendor,
    isLoading,
    error,
  } = useAdminVendor(vendorId, {
    showTestAccounts,
    enabled: isOpen && !!vendorId,
  });
  const createVendorMutation = useCreateVendor();
  const updateVendorMutation = useUpdateVendor();

  /**
   * Handle backdrop click - close modal
   */
  const handleOverlayClick = (e) => {
    if (e.target.classList.contains("vendor-modal-backdrop")) {
      onClose();
    }
  };

  /**
   * Open vendor website in new tab
   */
  const handleOpenWebsite = () => {
    if (vendor?.website) {
      const url = vendor.website.startsWith("http")
        ? vendor.website
        : `https://${vendor.website}`;
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="vendor-modal-backdrop" onClick={handleOverlayClick}>
      <div className="vendor-modal">
        {/* Modal Header */}
        <div className="vendor-modal-header">
          <div className="vendor-modal-header-content">
            <div className="vendor-modal-header-icon">
              <FaTools />
            </div>
            <h2 className="vendor-modal-title">Vendor Information</h2>
          </div>
          <div className="vendor-modal-header-actions">
            {vendor && !isLoading && (
              <button
                className="vendor-modal-edit-btn"
                onClick={() => setShowEditModal(true)}
                data-tooltip="Edit Vendor"
              >
                <FaEdit />
                <span>Edit</span>
              </button>
            )}
            <button
              className="vendor-modal-close"
              onClick={onClose}
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="vendor-modal-body">
          {isLoading ? (
            <div className="vendor-modal-loading">
              <FaSpinner className="spinning" />
              <p>Loading vendor information...</p>
            </div>
          ) : error ? (
            <div className="vendor-modal-error">
              <FaExclamationTriangle />
              <p>Error loading vendor: {error.message}</p>
            </div>
          ) : vendor ? (
            <>
              {/* Vendor Name, Category & Status */}
              <div className="vendor-modal-section">
                <div className="vendor-modal-header-section">
                  {/* Logo */}
                  {vendor.logo && vendor.logo.length > 0 && (
                    <img
                      src={
                        vendor.logo[0].thumbnails?.large?.url ||
                        vendor.logo[0].url
                      }
                      alt={`${vendor.vendorName} logo`}
                      className="vendor-modal-logo"
                    />
                  )}
                  <div className="vendor-modal-header-row">
                    <h3 className="vendor-modal-name">{vendor.vendorName}</h3>
                  </div>
                  <div className="vendor-modal-header-content">
                    <div className="vendor-modal-badges">
                      {vendor.vendorCategory && (
                        <span className="vendor-modal-type-badge">
                          {Array.isArray(vendor.vendorCategory)
                            ? vendor.vendorCategory.join(", ")
                            : vendor.vendorCategory}
                        </span>
                      )}
                      <span
                        className={`vendor-modal-status ${
                          vendor.status === "Active"
                            ? "status-active"
                            : "status-inactive"
                        }`}
                      >
                        {vendor.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Person */}
              {vendor.contactName && (
                <div className="vendor-modal-section">
                  <h4 className="vendor-modal-section-title">Contact Person</h4>
                  <div className="vendor-modal-contact-card">
                    <FaUserTie className="vendor-modal-contact-icon" />
                    <span className="vendor-modal-contact-name">
                      {vendor.contactName}
                    </span>
                  </div>
                </div>
              )}

              {/* Contact Information */}
              <div className="vendor-modal-section">
                <h4 className="vendor-modal-section-title">Contact Info</h4>
                <div className="vendor-modal-contact-card">
                  <div className="vendor-modal-info-grid">
                    {vendor.contactPhone && (
                      <div className="vendor-modal-info-row">
                        <FaPhone className="vendor-modal-icon" />
                        <div>
                          <div className="vendor-modal-label">Phone</div>
                          <div className="vendor-modal-value">
                            <a
                              href={`tel:${vendor.contactPhone}`}
                              data-tooltip="Call or Text"
                            >
                              {formatPhoneDisplay(vendor.contactPhone)}
                            </a>
                          </div>
                        </div>
                      </div>
                    )}

                    {vendor.contactEmail && (
                      <div className="vendor-modal-info-row">
                        <FaEnvelope className="vendor-modal-icon" />
                        <div>
                          <div className="vendor-modal-label">Email</div>
                          <div className="vendor-modal-value">
                            <a
                              href={`mailto:${vendor.contactEmail}`}
                              data-tooltip="Send Email"
                            >
                              {vendor.contactEmail}
                            </a>
                          </div>
                        </div>
                      </div>
                    )}

                    {vendor.address && (
                      <div className="vendor-modal-info-row">
                        <FaMapMarkerAlt className="vendor-modal-icon" />
                        <div>
                          <div className="vendor-modal-label">Address</div>
                          <div className="vendor-modal-value">
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(vendor.address)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-tooltip="Open in Google Maps"
                            >
                              {vendor.address}
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Website */}
              {vendor.website && (
                <div className="vendor-modal-section">
                  <h4 className="vendor-modal-section-title">Website</h4>
                  <div className="vendor-modal-website-card">
                    <div className="vendor-modal-website-info">
                      <FaGlobe className="vendor-modal-website-icon" />
                      <span className="vendor-modal-website-url">
                        {vendor.website}
                      </span>
                    </div>
                    <button
                      className="vendor-modal-website-btn"
                      onClick={handleOpenWebsite}
                    >
                      <span>Visit Website</span>
                      <FaExternalLinkAlt />
                    </button>
                  </div>
                </div>
              )}

              {/* Additional Details */}
              {(vendor.isClientFacing ||
                vendor.hasShowroom ||
                vendor.vendorType) && (
                <div className="vendor-modal-section">
                  <h4 className="vendor-modal-section-title">
                    Additional Details
                  </h4>
                  <div className="vendor-modal-details-grid">
                    {vendor.vendorType && (
                      <span className="vendor-modal-type-badge">
                        {Array.isArray(vendor.vendorType)
                          ? vendor.vendorType.join(" / ")
                          : vendor.vendorType}
                      </span>
                    )}
                    {vendor.isClientFacing && (
                      <div className="vendor-modal-detail-chip">
                        <FaUsers /> Client Facing
                      </div>
                    )}
                    {vendor.hasShowroom && (
                      <div className="vendor-modal-detail-chip">
                        <FaStore /> Has Showroom
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {vendor.notes && (
                <div className="vendor-modal-section">
                  <h4 className="vendor-modal-section-title">
                    <FaStickyNote className="vendor-modal-icon-inline" />
                    Notes
                  </h4>
                  <div className="vendor-modal-notes">{vendor.notes}</div>
                </div>
              )}

              {/* Attachments */}
              {vendor.attachments && vendor.attachments.length > 0 && (
                <div className="vendor-modal-section">
                  <h4 className="vendor-modal-section-title">
                    <FaPaperclip className="vendor-modal-icon-inline" />
                    Attachments ({vendor.attachments.length})
                  </h4>
                  <div className="vendor-modal-attachments">
                    {vendor.attachments.map((attachment, index) => (
                      <a
                        key={index}
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="vendor-modal-attachment-item"
                      >
                        <FaDownload className="vendor-modal-attachment-icon" />
                        <span className="vendor-modal-attachment-name">
                          {attachment.filename}
                        </span>
                        <span className="vendor-modal-attachment-size">
                          {formatFileSize(attachment.size)}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Assigned Projects */}
              {vendor.assignedProjects && vendor.assignedProjects.length > 0 && (
                <div className="vendor-modal-section">
                  <h4 className="vendor-modal-section-title">
                    <FaProjectDiagram className="vendor-modal-icon-inline" />
                    Assigned Projects ({vendor.assignedProjects.length})
                  </h4>
                  {vendor.assignedProjects.map((project) => (
                    <div
                      key={project.id}
                      className="vendor-modal-project-card"
                    >
                      <span className="vendor-modal-project-name">
                        {project.name}
                      </span>
                      <button
                        className="vendor-modal-project-btn"
                        onClick={() => {
                          onClose();
                          navigate(`/admin/projects/${project.id}`);
                        }}
                      >
                        <span>View</span>
                        <FaExternalLinkAlt />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Modal Footer */}
        <div className="vendor-modal-footer">
          <button
            className="vendor-modal-btn-secondary"
            onClick={onClose}
            aria-label="Close"
          >
            Close
          </button>
        </div>
      </div>

      {/* Edit Vendor Modal */}
      <VendorModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        mode="edit"
        recordId={vendorId}
        vendorData={vendor}
        mutations={{
          createVendor: createVendorMutation,
          updateVendor: updateVendorMutation,
        }}
      />
    </div>
  );
};

export default VendorInfoModal;
